export type BookingPersonSlug = "rayk" | "katrin";

export type BookingPersonRequest = {
  slug: BookingPersonSlug | null;
  fixedByHost: boolean;
};

export function bookingPersonRequest(
  search: string = window.location.search,
  hostname: string = window.location.hostname,
): BookingPersonRequest {
  const explicit = new URLSearchParams(search).get("person");
  if (explicit === "rayk" || explicit === "katrin") {
    return { slug: explicit, fixedByHost: false };
  }

  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (normalizedHost === "book.katrinschoerling.com") {
    return { slug: "katrin", fixedByHost: true };
  }
  if (normalizedHost === "book.raykschoerling.com") {
    return { slug: "rayk", fixedByHost: true };
  }

  return { slug: null, fixedByHost: false };
}

export function shouldOpenPractitionerBookingAtRoot(
  search: string = window.location.search,
  hostname: string = window.location.hostname,
) {
  const request = bookingPersonRequest(search, hostname);
  return request.fixedByHost;
}

// Each individual's own domain, used to send someone who landed on the
// wrong practitioner's site to the right one. The joint domain is where
// Katrin & Rayk's shared events live.
export const RAYK_SITE_URL = "https://book.raykschoerling.com/";
export const KATRIN_SITE_URL = "https://book.katrinschoerling.com/";
export const BOTH_SITE_URL = "https://book.katrinundrayk.de/";

export function otherPersonSiteUrl(slug: BookingPersonSlug): string {
  return slug === "rayk" ? KATRIN_SITE_URL : RAYK_SITE_URL;
}

export function isJointEventsHost(hostname: string = window.location.hostname) {
  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");
  return normalizedHost === "book.katrinundrayk.de";
}

export function shouldOpenJointEventsAtRoot(
  pathname: string = window.location.pathname,
  search: string = window.location.search,
  hostname: string = window.location.hostname,
) {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const section = new URLSearchParams(search).get("bereich");
  return isJointEventsHost(hostname) && normalizedPath === "/" && !section;
}

export function shouldKeepPersonalBookingOffJointHost(
  pathname: string = window.location.pathname,
  hostname: string = window.location.hostname,
) {
  if (!isJointEventsHost(hostname)) return false;
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  return ["/termine", "/online", "/berlin"].includes(normalizedPath);
}


export type PublicSiteIdentity = {
  brand: string;
  domain: string;
  siteUrl: string;
  contactMail: string;
};

export function publicSiteIdentity(
  hostname: string = window.location.hostname,
): PublicSiteIdentity {
  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");

  if (normalizedHost === "book.katrinschoerling.com") {
    return {
      brand: "Katrin Schörling",
      domain: "book.katrinschoerling.com",
      siteUrl: KATRIN_SITE_URL,
      contactMail: "booking@katrinschoerling.com",
    };
  }

  if (normalizedHost === "book.katrinundrayk.de") {
    return {
      brand: "Katrin & Rayk Schörling",
      domain: "book.katrinundrayk.de",
      siteUrl: BOTH_SITE_URL,
      contactMail: "hello@katrinundrayk.de",
    };
  }

  return {
    brand: "Rayk Schörling",
    domain: "book.raykschoerling.com",
    siteUrl: RAYK_SITE_URL,
    contactMail: "booking@raykschoerling.com",
  };
}


export function adminEntryUrl(hostname: string = window.location.hostname) {
  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");
  const url = new URL(BOTH_SITE_URL);

  if (normalizedHost === "book.katrinschoerling.com") {
    url.search = "?admin=1&person=katrin";
  } else if (normalizedHost === "book.katrinundrayk.de") {
    url.search = "?admin=events";
  } else {
    url.search = "?admin=1&person=rayk";
  }

  return url.toString();
}

export function shouldRedirectAdminToSharedHost(
  hostname: string = window.location.hostname,
) {
  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");
  return (
    normalizedHost === "book.raykschoerling.com" ||
    normalizedHost === "book.katrinschoerling.com"
  );
}

export function sharedAdminUrl(
  search: string = window.location.search,
  hash: string = window.location.hash,
) {
  const url = new URL(BOTH_SITE_URL);
  url.search = search;
  url.hash = hash;
  return url.toString();
}
